import { ReactElement, useState, useEffect } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { BackrsIcon } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';
import { Tier } from '@backrs_ui/common-ui/src/models/careerPath';
import { Internship } from '@backrs_ui/common-ui/src/models/internship';
import {
  ASK_LUBAV_BUTTON_ENDPOINTS,
  AskLubavButtonEndpoint,
} from '@backrs_ui/common-ui/src/models/nugget';
import { attemptToFixJson } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/askLubavApi.util';
import { PanelNugget } from '@backrs_ui/common-ui/src/molecules/Nugget/PanelNugget';

import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Typography, Button } from '@mui/material';
import { SxProps } from '@mui/system';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type ApiKeyAction = {
  key: AskLubavButtonEndpoint | undefined;
  onClick?: () => void;
  actionButtonText?: string;
} | null;

export type AskLubavResponseProps = Readonly<{
  response: string | undefined;
  tier: Tier | undefined;
  apiKeyAction: ApiKeyAction | null;
  containerSx?: SxProps;
  contentSize?: 'LARGE' | 'SMALL';
}>;

type LubavResponse = Readonly<{
  introductory_paragraph: string;
  headers: string[];
  bodies: string[];
}>;

type LubavResponseWithNuggets = LubavResponse & {
  nuggets: {
    internships: {
      relevance: string;
      metadata: Internship;
    }[];
  };
};
const defaultLubavResponse: LubavResponse = {
  introductory_paragraph: '',
  headers: [],
  bodies: [],
};

export const AskLubavResponse = ({
  response,
  tier,
  apiKeyAction,
  containerSx,
  contentSize = 'LARGE',
}: AskLubavResponseProps): ReactElement => {
  const [parsedResponse, setParsedResponse] = useState<LubavResponse>(defaultLubavResponse);
  const [parsedResponseWithNuggets, setParsedResponseWithNuggets] = useState<
    LubavResponseWithNuggets | undefined
  >(undefined);

  const [openSections, setOpenSections] = useState<boolean[]>([]);

  useEffect(() => {
    if (parsedResponse.headers) {
      setOpenSections(
        parsedResponse.headers.map((header) => {
          if (apiKeyAction?.key === ASK_LUBAV_BUTTON_ENDPOINTS.CAREER_TRACK_ROLES_BY_TRACK) {
            // expand only the tier that the user got for roles_by_track
            return tier ? header === tier : false;
          }

          // otherwise default to keeping all sections expanded
          return true;
        }),
      );
    }
  }, [parsedResponse.headers, apiKeyAction, tier]);

  const toggleSection = (index: number) => {
    setOpenSections((prev) => {
      const newOpenSections = [...prev];

      newOpenSections[index] = !newOpenSections[index];

      return newOpenSections;
    });
  };

  useEffect(() => {
    if (response) {
      const fixedJson = attemptToFixJson(response, defaultLubavResponse);

      if (fixedJson) {
        try {
          setParsedResponse(JSON.parse(fixedJson));
        } catch (error) {
          console.error('Failed to parse JSON after fixing:', error);
          setParsedResponse(defaultLubavResponse);
        }
      }

      try {
        const parsed = JSON.parse(response) as LubavResponseWithNuggets;

        setParsedResponseWithNuggets(parsed);
      } catch {
        setParsedResponseWithNuggets(undefined);
      }
    }
  }, [response]);

  const markdownComponents: Components = {
    table: ({ ...props }) => <table style={tableStyle} {...props} />,
    th: ({ ...props }) => <th style={tableHeaderStyle} {...props} />,
    td: ({ ...props }) => <td style={tableCellStyle} {...props} />,
    a: ({ ...props }) => (
      <a style={linkStyle} target="_blank" rel="noopener noreferrer" {...props} />
    ),
    p: ({ ...props }) => <p style={paragraphStyle(contentSize)} {...props} />,
    li: ({ ...props }) => <li style={listItemStyle(contentSize)} {...props} />,
  };

  if (!parsedResponse) {
    return <></>;
  }

  return (
    <Box sx={{ ...containerStyle, ...containerSx } as SxProps}>
      {/* Intro Paragraph */}
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {parsedResponse.introductory_paragraph || ''}
      </ReactMarkdown>

      {/* Headers & Bodies */}
      {parsedResponse.headers?.map((header: string, index: number) => {
        const body = parsedResponse.bodies?.[index] || ''; // Ensure `bodies[index]` exists

        return (
          <Box key={index} sx={{ mt: '0.2em' }}>
            {header && (
              <Typography onClick={() => toggleSection(index)} sx={typographyStyle(contentSize)}>
                {header}
                <KeyboardArrowDown sx={getArrowIconStyle(openSections[index])} />
              </Typography>
            )}
            {openSections[index] && (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {body}
              </ReactMarkdown>
            )}
          </Box>
        );
      }) || []}

      {parsedResponseWithNuggets?.nuggets?.internships && (
        <Box sx={internshipContainerStyle}>
          {parsedResponseWithNuggets.nuggets.internships.map((internship, index) => (
            <Box key={index} sx={{ mb: 4 }}>
              <PanelNugget
                title={internship.metadata.role_title}
                variant={{
                  type: 'internship',
                  internshipInfo: internship.metadata,
                }}
                body={internship.metadata.ai_description}
                subText={internship.relevance}
                size="SMALL"
              />
            </Box>
          ))}
        </Box>
      )}

      {/* Marker of Success Button */}
      {apiKeyAction?.onClick && apiKeyAction.actionButtonText && (
        <Button variant="contained" onClick={apiKeyAction.onClick} sx={markersOfSuccessButtonStyle}>
          <Typography sx={markersOfSuccessTextStyle}>{apiKeyAction.actionButtonText}</Typography>
          <BackrsIcon
            name="IconArrowOpenRight"
            alt="IconArrowRight"
            style={markersOfSuccessIconStyle}
          />
        </Button>
      )}
    </Box>
  );
};

const containerStyle: React.CSSProperties = {
  backgroundColor: appColors.color.blue10,
  padding: '.5rem',
  paddingLeft: '24px',
  paddingRight: '24px',
  borderRadius: '9px',
  boxSizing: 'border-box',
};

const tableStyle: React.CSSProperties = {
  borderCollapse: 'separate',
  borderSpacing: 0,
  width: '100%',
  borderTop: `2px solid ${appColors.neutral.black}`,
  borderBottom: `2px solid ${appColors.neutral.black}`,
  marginTop: '22px',
};

const tableHeaderStyle: React.CSSProperties = {
  border: 'none',
  padding: '4px 8px',
  color: appColors.neutral.black,
  borderBottom: `2px solid ${appColors.neutral.black}`,
  textAlign: 'left',
};

const tableCellStyle: React.CSSProperties = {
  border: 'none',
  padding: '4px 8px',
  whiteSpace: 'nowrap',
  width: 'fit-content',
};

const linkStyle: React.CSSProperties = {
  color: appColors.color.blue1,
  fontWeight: 'bold',
};

const typographyStyle = (contentSize: 'LARGE' | 'SMALL'): React.CSSProperties => ({
  fontWeight: 'bold',
  fontSize: contentSize === 'LARGE' ? '20px' : '16px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
});

const markersOfSuccessButtonStyle: SxProps = {
  backgroundColor: appColors.color.blue1,
  borderRadius: '9px',
  cursor: 'pointer',
  px: '16px',
  py: '8px',
  '&:hover': {
    backgroundColor: appColors.color.blue2,
    boxShadow: 'none',
  },
  boxShadow: 'none',
};

const markersOfSuccessTextStyle: SxProps = {
  fontSize: '16px',
  textTransform: 'none',
  fontWeight: 700,
  color: '#FFFFFF',
};

const markersOfSuccessIconStyle: React.CSSProperties = {
  width: '16px',
  height: '16px',
  marginLeft: '12px',
  verticalAlign: 'middle',
};

const getArrowIconStyle = (isOpen: boolean): SxProps =>
  ({
    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
    transition: 'transform 0.2s ease-in-out',
    marginLeft: '4px',
  }) as const;

const internshipContainerStyle: React.CSSProperties = {
  backgroundColor: '#FFF',
  borderRadius: '9px',
  padding: '16px',
};

const paragraphStyle = (contentSize: 'LARGE' | 'SMALL'): React.CSSProperties => ({
  fontSize: contentSize === 'LARGE' ? '16px' : '14px',
});

const listItemStyle = (contentSize: 'LARGE' | 'SMALL'): React.CSSProperties => ({
  fontSize: contentSize === 'LARGE' ? '16px' : '14px',
  marginBottom: '0.5em',
});
